/** @file gizmoAccess: which handles a tool may draw and grab on the selection, or why the gizmo is locked. */
import type { Clip } from '@zilo/pose3d-format/types/clip';
import type { EditorState, Tool } from '../../state/editorTypes';
import { editLock } from './editLock';
import { startKeyOf } from './startKeyOf';

/**
 * Bit `h` of `show` / `pick` = handle `h` (`GizmoHandle`: 0..2 axes, 3 centre, 4..6 planes) is drawn / can be
 * grabbed; `lock` the text that stands where the gizmo centre is (nothing is drawn); `note` the status line's word
 * on a reduced handle set.
 */
export type GizmoAccess = { show: number; pick: number; lock: string | null; note: string | null };

const NONE: GizmoAccess = { show: 0, pick: 0, lock: null, note: null };
const RINGS = 0b0001111;
const ALL = 0b1111111;
const CENTRE = 0b0001000;

/** What a tool's rule reads: the document's nodes, the playhead clip and frame, the selected names. */
type Scope = { nodes: Record<string, { parent?: string; ik?: number }>; clip: Clip; frame: number; names: readonly string[] };

/** A root, or a node whose key at the frame (or the interpolated one) holds a translation. */
const movable = (sc: Scope, name: string): boolean => sc.nodes[name]?.parent === undefined || startKeyOf(sc.clip, name, sc.frame).pos !== undefined;

/** The nodes with an IK chain ending at them, for the IK note. */
const ikEnds = (sc: Scope): string[] => Object.entries(sc.nodes).filter(([, n]) => (n.ik ?? 0) > 0).map(([name]) => name);

/** One rule per tool: rings for rotate, the centre cube for scale (one uniform scale), moves only where they key. */
const RULES: Record<Tool, (sc: Scope) => GizmoAccess> = {
  hand: () => NONE,
  rotate: () => ({ show: RINGS, pick: RINGS, lock: null, note: null }),
  scale: () => ({ show: CENTRE, pick: CENTRE, lock: null, note: null }),
  move: (sc) => (sc.names.every((n) => movable(sc, n))
    ? { show: ALL, pick: ALL, lock: null, note: null }
    : { show: CENTRE, pick: 0, lock: null, note: 'move: root and keyed translations only' }),
  ik: (sc) => ((sc.nodes[sc.names.at(-1) ?? '']?.ik ?? 0) > 0
    ? { show: CENTRE, pick: 0, lock: null, note: null }
    : { show: 0, pick: 0, lock: null, note: `IK: drag a joint with an IK chain (${ikEnds(sc).join(', ') || 'none in this puppet'})` }),
};

/**
 * The handles `tool` offers on the current selection: none for the hand tool, an empty selection or Quads mode (the
 * viewport draws the rest pose there and its handles edit the rig, not the clip); all locked
 * (with `editLock`'s reason) when the playhead cannot take a key; otherwise the tool's rule (`RULES`).
 */
export function gizmoAccess(s: Pick<EditorState, 'playhead' | 'puppet' | 'selection' | 'camera'>, tool: Tool): GizmoAccess {
  const names = s.selection.nodes;
  const doc = s.puppet.doc;
  if (tool === 'hand' || names.length === 0 || doc === null || s.camera.quads) return NONE;
  const lock = editLock(s);
  const clip = s.playhead.clip === null ? undefined : doc.clips[s.playhead.clip];
  if (lock !== null || clip === undefined) return { show: 0, pick: 0, lock, note: null };
  return RULES[tool]({ nodes: doc.puppet.nodes, clip, frame: s.playhead.frame, names });
}
